/**
 * Standard States and Transitions ENUMs
 * 
 * Defines standard state names and transitions for consistent use across the application.
 * These ENUMs ensure that state and transition names are consistent at all levels:
 * - Global properties
 * - State templates
 * - Prime artifacts
 * - Company artifacts
 */

// ============================================================================
// Standard Operational States
// ============================================================================

export enum StandardOperationalState {
  INITIALIZED = 'INITIALIZED',
  CREATED = 'CREATED',
  DRAFTED = 'DRAFTED',
  ACTIVE = 'ACTIVE',
  CLOSED = 'CLOSED'
}

// ============================================================================
// Standard Improvement States
// ============================================================================

export enum StandardImprovementState {
  IDENTIFIED = 'IDENTIFIED',
  DEFINED = 'DEFINED',
  DESIGNED = 'DESIGNED',
  IMPLEMENTED = 'IMPLEMENTED',
  VALIDATED = 'VALIDATED'
}

// ============================================================================
// Standard Transitions
// ============================================================================

export enum StandardTransition {
  // Operational Forward Transitions
  INITIALIZED_TO_CREATED = 'INITIALIZED_TO_CREATED',
  CREATED_TO_DRAFTED = 'CREATED_TO_DRAFTED',
  DRAFTED_TO_ACTIVE = 'DRAFTED_TO_ACTIVE',
  ACTIVE_TO_CLOSED = 'ACTIVE_TO_CLOSED',
  
  // Operational Backward Transitions
  CREATED_TO_INITIALIZED = 'CREATED_TO_INITIALIZED',
  DRAFTED_TO_CREATED = 'DRAFTED_TO_CREATED',
  ACTIVE_TO_DRAFTED = 'ACTIVE_TO_DRAFTED',
  CLOSED_TO_ACTIVE = 'CLOSED_TO_ACTIVE',
  
  // Improvement Forward Transitions
  IDENTIFIED_TO_DEFINED = 'IDENTIFIED_TO_DEFINED',
  DEFINED_TO_DESIGNED = 'DEFINED_TO_DESIGNED',
  DESIGNED_TO_IMPLEMENTED = 'DESIGNED_TO_IMPLEMENTED',
  IMPLEMENTED_TO_VALIDATED = 'IMPLEMENTED_TO_VALIDATED',
  
  // Improvement Backward Transitions
  DEFINED_TO_IDENTIFIED = 'DEFINED_TO_IDENTIFIED',
  DESIGNED_TO_DEFINED = 'DESIGNED_TO_DEFINED',
  IMPLEMENTED_TO_DESIGNED = 'IMPLEMENTED_TO_DESIGNED',
  VALIDATED_TO_IMPLEMENTED = 'VALIDATED_TO_IMPLEMENTED'
}

// ============================================================================
// Type Definitions
// ============================================================================

export type StandardState = StandardOperationalState | StandardImprovementState;
export type StateType = 'operational' | 'improvement';

export interface StandardStateInfo {
  name: string;
  type: StateType;
  isInitial: boolean;
  isFinal: boolean;
}

export interface StandardTransitionInfo {
  name: string;
  fromState: string;
  toState: string;
  type: StateType;
  isForward: boolean;
}

// ============================================================================
// State Type Guards and Utilities
// ============================================================================

/**
 * Check if a state string is an operational state
 */
export function isOperationalState(state: string): boolean {
  return Object.values(StandardOperationalState).includes(state as StandardOperationalState);
}

/**
 * Check if a state string is an improvement state
 */
export function isImprovementState(state: string): boolean {
  return Object.values(StandardImprovementState).includes(state as StandardImprovementState);
}

/**
 * Get the type of a state (operational or improvement)
 */
export function getStateType(state: string): StateType | null {
  if (isOperationalState(state)) {
    return 'operational';
  }
  if (isImprovementState(state)) {
    return 'improvement';
  }
  return null;
}

/**
 * Check if two states are of the same type
 */
export function areStatesSameType(state1: string, state2: string): boolean {
  const type1 = getStateType(state1);
  const type2 = getStateType(state2);
  return type1 !== null && type2 !== null && type1 === type2;
}

// ============================================================================
// State Ordering and Adjacency
// ============================================================================

const OPERATIONAL_STATE_ORDER: StandardOperationalState[] = [
  StandardOperationalState.INITIALIZED,
  StandardOperationalState.CREATED,
  StandardOperationalState.DRAFTED,
  StandardOperationalState.ACTIVE,
  StandardOperationalState.CLOSED
];

const IMPROVEMENT_STATE_ORDER: StandardImprovementState[] = [
  StandardImprovementState.IDENTIFIED,
  StandardImprovementState.DEFINED,
  StandardImprovementState.DESIGNED,
  StandardImprovementState.IMPLEMENTED,
  StandardImprovementState.VALIDATED
];

/**
 * Get the order index of an operational state
 */
export function getOperationalStateOrder(state: StandardOperationalState): number {
  return OPERATIONAL_STATE_ORDER.indexOf(state);
}

/**
 * Get the order index of an improvement state
 */
export function getImprovementStateOrder(state: StandardImprovementState): number {
  return IMPROVEMENT_STATE_ORDER.indexOf(state);
}

/**
 * Get the next state in the operational flow
 */
export function getNextOperationalState(state: StandardOperationalState): StandardOperationalState | null {
  const index = getOperationalStateOrder(state);
  if (index === -1 || index === OPERATIONAL_STATE_ORDER.length - 1) {
    return null;
  }
  return OPERATIONAL_STATE_ORDER[index + 1];
}

/**
 * Get the previous state in the operational flow
 */
export function getPreviousOperationalState(state: StandardOperationalState): StandardOperationalState | null {
  const index = getOperationalStateOrder(state);
  if (index <= 0) {
    return null;
  }
  return OPERATIONAL_STATE_ORDER[index - 1];
}

/**
 * Get the next state in the improvement flow
 */
export function getNextImprovementState(state: StandardImprovementState): StandardImprovementState | null {
  const index = getImprovementStateOrder(state);
  if (index === -1 || index === IMPROVEMENT_STATE_ORDER.length - 1) {
    return null;
  }
  return IMPROVEMENT_STATE_ORDER[index + 1];
}

/**
 * Get the previous state in the improvement flow
 */
export function getPreviousImprovementState(state: StandardImprovementState): StandardImprovementState | null {
  const index = getImprovementStateOrder(state);
  if (index <= 0) {
    return null;
  }
  return IMPROVEMENT_STATE_ORDER[index - 1];
}

/**
 * Check if two states are adjacent in the flow
 */
export function areStatesAdjacent(state1: string, state2: string): boolean {
  if (!areStatesSameType(state1, state2)) {
    return false;
  }
  
  const type = getStateType(state1);
  if (type === 'operational') {
    const next = getNextOperationalState(state1 as StandardOperationalState);
    const prev = getPreviousOperationalState(state1 as StandardOperationalState);
    return state2 === next || state2 === prev;
  } else if (type === 'improvement') {
    const next = getNextImprovementState(state1 as StandardImprovementState);
    const prev = getPreviousImprovementState(state1 as StandardImprovementState);
    return state2 === next || state2 === prev;
  }
  
  return false;
}

// ============================================================================
// Transition Generation
// ============================================================================

/**
 * Get all forward transitions for a state type
 */
export function getForwardTransitions(stateType: StateType): StandardTransitionInfo[] {
  if (stateType === 'operational') {
    return [
      {
        name: 'Initialize',
        fromState: StandardOperationalState.INITIALIZED,
        toState: StandardOperationalState.CREATED,
        type: 'operational',
        isForward: true
      },
      {
        name: 'Create',
        fromState: StandardOperationalState.CREATED,
        toState: StandardOperationalState.DRAFTED,
        type: 'operational',
        isForward: true
      },
      {
        name: 'Draft',
        fromState: StandardOperationalState.DRAFTED,
        toState: StandardOperationalState.ACTIVE,
        type: 'operational',
        isForward: true
      },
      {
        name: 'Activate',
        fromState: StandardOperationalState.ACTIVE,
        toState: StandardOperationalState.CLOSED,
        type: 'operational',
        isForward: true
      }
    ];
  } else {
    return [
      {
        name: 'Identify',
        fromState: StandardImprovementState.IDENTIFIED,
        toState: StandardImprovementState.DEFINED,
        type: 'improvement',
        isForward: true
      },
      {
        name: 'Define',
        fromState: StandardImprovementState.DEFINED,
        toState: StandardImprovementState.DESIGNED,
        type: 'improvement',
        isForward: true
      },
      {
        name: 'Design',
        fromState: StandardImprovementState.DESIGNED,
        toState: StandardImprovementState.IMPLEMENTED,
        type: 'improvement',
        isForward: true
      },
      {
        name: 'Implement',
        fromState: StandardImprovementState.IMPLEMENTED,
        toState: StandardImprovementState.VALIDATED,
        type: 'improvement',
        isForward: true
      }
    ];
  }
}

/**
 * Get all backward transitions for a state type
 */
export function getBackwardTransitions(stateType: StateType): StandardTransitionInfo[] {
  if (stateType === 'operational') {
    return [
      {
        name: 'Reset to Initialized',
        fromState: StandardOperationalState.CREATED,
        toState: StandardOperationalState.INITIALIZED,
        type: 'operational',
        isForward: false
      },
      {
        name: 'Reset to Created',
        fromState: StandardOperationalState.DRAFTED,
        toState: StandardOperationalState.CREATED,
        type: 'operational',
        isForward: false
      },
      {
        name: 'Reset to Drafted',
        fromState: StandardOperationalState.ACTIVE,
        toState: StandardOperationalState.DRAFTED,
        type: 'operational',
        isForward: false
      },
      {
        name: 'Reopen',
        fromState: StandardOperationalState.CLOSED,
        toState: StandardOperationalState.ACTIVE,
        type: 'operational',
        isForward: false
      }
    ];
  } else {
    return [
      {
        name: 'Reset to Identified',
        fromState: StandardImprovementState.DEFINED,
        toState: StandardImprovementState.IDENTIFIED,
        type: 'improvement',
        isForward: false
      },
      {
        name: 'Reset to Defined',
        fromState: StandardImprovementState.DESIGNED,
        toState: StandardImprovementState.DEFINED,
        type: 'improvement',
        isForward: false
      },
      {
        name: 'Reset to Designed',
        fromState: StandardImprovementState.IMPLEMENTED,
        toState: StandardImprovementState.DESIGNED,
        type: 'improvement',
        isForward: false
      },
      {
        name: 'Reset to Implemented',
        fromState: StandardImprovementState.VALIDATED,
        toState: StandardImprovementState.IMPLEMENTED,
        type: 'improvement',
        isForward: false
      }
    ];
  }
}

/**
 * Get all transitions (forward and backward) for a state type
 */
export function getAllTransitions(stateType: StateType): StandardTransitionInfo[] {
  return [
    ...getForwardTransitions(stateType),
    ...getBackwardTransitions(stateType)
  ];
}

/**
 * Get transition info for a specific from/to state pair
 */
export function getTransitionInfo(fromState: string, toState: string): StandardTransitionInfo | null {
  if (!areStatesSameType(fromState, toState)) {
    return null;
  }
  
  const type = getStateType(fromState);
  if (!type) {
    return null;
  }
  
  const allTransitions = getAllTransitions(type);
  return allTransitions.find(t => t.fromState === fromState && t.toState === toState) || null;
}

/**
 * Get all states for a given state type
 */
export function getStatesForType(stateType: StateType): string[] {
  if (stateType === 'operational') {
    return Object.values(StandardOperationalState);
  } else {
    return Object.values(StandardImprovementState);
  }
}

/**
 * Get state info including whether it's initial or final
 */
export function getStateInfo(state: string): StandardStateInfo | null {
  const type = getStateType(state);
  if (!type) {
    return null;
  }
  
  let isInitial = false;
  let isFinal = false;
  
  if (type === 'operational') {
    isInitial = state === StandardOperationalState.INITIALIZED;
    isFinal = state === StandardOperationalState.CLOSED;
  } else {
    isInitial = state === StandardImprovementState.IDENTIFIED;
    isFinal = state === StandardImprovementState.VALIDATED;
  }
  
  return {
    name: state,
    type,
    isInitial,
    isFinal
  };
}

